import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Grid,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import {
  Edit as EditIcon,
  FolderOpen as FolderOpenIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import { PageHeader, FlexRow, FlexBetween, CodeBlock, SectionHeader } from '@/components/styled'
import type { Backend, VirtualBucket, Account } from '@/types'

interface Props {
  backend: Backend & { access_key?: string }
  virtualBuckets: VirtualBucket[]
}

const serviceLabels: Record<string, string> = {
  aws: 'AWS S3',
  minio: 'MinIO',
  other: 'S3-Compatible',
}

export default function BackendsShow({ backend, virtualBuckets }: Props) {
  const { currentAccount, isAccountAdmin } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
  }>().props

  const isLocalBackend = backend.backend_type === 'local'

  return (
    <ManagementLayout>
      <PageHeader
        title={backend.name}
        breadcrumb={
          <Breadcrumbs>
            <Link href={`/accounts/${currentAccount.slug}/backends`}>Backends</Link>
            <Typography color="text.primary">{backend.name}</Typography>
          </Breadcrumbs>
        }
        action={
          <FlexRow sx={{ gap: 1 }}>
            <Button
              component={Link}
              href={`/accounts/${currentAccount.slug}/backends/${backend.slug}/browser`}
              variant="contained"
              startIcon={<FolderOpenIcon />}
            >
              Browse Files
            </Button>
            {isAccountAdmin && (
              <Button
                component={Link}
                href={`/accounts/${currentAccount.slug}/backends/${backend.slug}/edit`}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                Edit Backend
              </Button>
            )}
          </FlexRow>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper>
            <Box sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Backend Details
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {isLocalBackend ? (
                      <Chip label="Local Filesystem" size="small" color="secondary" />
                    ) : (
                      <Chip
                        label={serviceLabels[backend.service] || 'S3-Compatible'}
                        size="small"
                        color={backend.service === 'aws' ? 'warning' : backend.service === 'minio' ? 'error' : 'default'}
                      />
                    )}
                  </Box>
                </Grid>

                {isLocalBackend ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Storage Path
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <CodeBlock>{backend.storage_path || '-'}</CodeBlock>
                    </Box>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Region
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {backend.region}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Bucket
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <CodeBlock>{backend.bucket}</CodeBlock>
                      </Box>
                    </Grid>
                    {backend.endpoint && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Endpoint
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <CodeBlock>{backend.endpoint}</CodeBlock>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        SSL Verification
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={backend.ssl_verify_peers ? 'Enabled' : 'Disabled'}
                          size="small"
                          color={backend.ssl_verify_peers ? 'success' : 'warning'}
                        />
                      </Box>
                    </Grid>
                    {backend.access_key && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Access Key
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <CodeBlock>{backend.access_key.substring(0, 4)}****</CodeBlock>
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper>
            <FlexBetween sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Virtual Buckets
              </Typography>
              {isAccountAdmin && (
                <Button
                  component={Link}
                  href={`/accounts/${currentAccount.slug}/backends/${backend.slug}/virtual_buckets/new`}
                  size="small"
                  startIcon={<AddIcon />}
                >
                  Add Virtual Bucket
                </Button>
              )}
            </FlexBetween>
            <Box sx={{ p: virtualBuckets.length > 0 ? 0 : 3 }}>
              {virtualBuckets.length > 0 ? (
                <List disablePadding>
                  {virtualBuckets.map((vb, index) => (
                    <ListItemButton
                      key={vb.id}
                      component={Link}
                      href={`/virtual_buckets/${vb.id}`}
                      divider={index < virtualBuckets.length - 1}
                    >
                      <ListItemText
                        primary={vb.name}
                        secondary={
                          vb.base_path ? (
                            <>
                              Base path: <CodeBlock>{vb.base_path}</CodeBlock>
                            </>
                          ) : (
                            'No base path'
                          )
                        }
                      />
                      <FlexRow sx={{ gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {vb.credentialsCount ?? 0} credentials
                        </Typography>
                        <ChevronRightIcon sx={{ color: 'grey.400' }} />
                      </FlexRow>
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No virtual buckets yet.
                  </Typography>
                  {isAccountAdmin && (
                    <Button
                      component={Link}
                      href={`/accounts/${currentAccount.slug}/backends/${backend.slug}/virtual_buckets/new`}
                      sx={{ mt: 2 }}
                      startIcon={<AddIcon />}
                    >
                      Create your first virtual bucket
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </ManagementLayout>
  )
}
